import React from "react";
import type { ReviewMedication } from "../../services/diagnosis.service";

interface MedicationCardProps {
  med: ReviewMedication;
  index: number;
  onConfirm: (id: string) => void;
  onEdit: (id: string) => void;
  onRemove: (id: string) => void;
  onOverrideChange: (id: string, reason: string) => void;
}

const MedicationCard: React.FC<MedicationCardProps> = ({
  med,
  index,
  onConfirm,
  onEdit,
  onRemove,
  onOverrideChange,
}) => {
  // Show the full red banner while the conflict has no override reason recorded,
  // or while an AI-suggested card is pending review.
  // Once an override reason is provided and confirmed, the allergy stays flagged
  // with a softer inline note so the doctor sees the override reason.
  const showOverrideBanner = med.allergy_conflict && (med.ai_suggested || !med.override_reason.trim());
  const showConfirmedAllergyNote = med.allergy_conflict && !med.ai_suggested && Boolean(med.override_reason.trim());

  return (
    <div className={`border rounded-2xl overflow-hidden ${med.allergy_conflict ? "border-red-300" : "border-slate-200"}`}>

      {/* Allergy conflict banner — pending state */}
      {showOverrideBanner && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-3 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <p className="text-sm font-semibold text-red-700">
              {med.conflicting_allergen
                ? `Life-threatening allergy — ${med.conflicting_allergen}`
                : "Allergy conflict detected — override reason required before confirming"}
            </p>
          </div>
          <input
            type="text"
            placeholder="Enter clinical reason for prescribing despite allergy…"
            value={med.override_reason}
            onChange={e => onOverrideChange(med.medication_id, e.target.value)}
            className="w-full h-9 px-3 text-base sm:text-sm border border-red-300 rounded-xl bg-white text-red-800 placeholder-red-300 outline-none focus:ring-2 focus:ring-red-300 transition-all"
          />
        </div>
      )}

      {/* Allergy resolved — compact note shown after override is confirmed */}
      {showConfirmedAllergyNote && (
        <div className="bg-red-50/60 border-b border-red-100 px-4 py-2 flex items-center gap-2">
          <svg className="w-3.5 h-3.5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span className="text-xs font-medium text-red-700">Allergy override</span>
          <span className="text-xs text-red-600/80 truncate flex-1">— {med.override_reason || "no reason recorded"}</span>
        </div>
      )}

      {/* Card body */}
      <div className="px-4 py-4 flex items-start gap-3">
        {/* Index */}
        <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-xs font-bold text-sky-600">{index + 1}</span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap mb-1">
            {/* Name + badge */}
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-bold text-slate-900 tracking-wide">{med.name}</p>
              {med.ai_suggested && !med.allergy_conflict ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  AI Suggested
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Confirmed
                </span>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {med.ai_suggested && (
                <button
                  onClick={() => onConfirm(med.medication_id)}
                  disabled={med.allergy_conflict && !med.override_reason.trim()}
                  className="px-2.5 py-1 text-xs font-semibold rounded-xl bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Confirm
                </button>
              )}
              <button
                onClick={() => onEdit(med.medication_id)}
                className="px-2.5 py-1 text-xs font-medium rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Edit
              </button>
              <button
                onClick={() => onRemove(med.medication_id)}
                className="px-2.5 py-1 text-xs font-medium rounded-xl border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
              >
                Remove
              </button>
            </div>
          </div>

          {/* Details row */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-slate-500">
            <span>{med.dosage}</span>
            {med.frequency && <span>· {med.frequency}</span>}
            {med.timing && <span>· {med.timing}</span>}
            {med.duration && <span>· {med.duration}</span>}
            {med.quantity != null && <span>· Qty: {med.quantity}</span>}
          </div>
          {med.instructions && (
            <p className="text-xs text-slate-400 mt-1">{med.instructions}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MedicationCard;
